<script setup lang="ts">
import { useQuery } from "@tanstack/vue-query";
import api from "~/utils/auth/api";
import { useSettings } from "~/store/settings";
import t from "~/utils/translate";
import Silk from "~/components/layout/TrackRacing/Shared/Silk.vue";
import OutcomesLoader from "~/components/layout/TrackRacing/Shared/Outcome/OutcomesLoader.vue";
import { IRaceResultsResponse, IResult, IPriceHistory } from "~/types/racing/race-results";

interface IResultsProps {
	eventId: number | undefined;
}

interface IResultWithPrices extends IResult {
	price?: IPriceHistory;
}

const settings = useSettings();
const props = defineProps<IResultsProps>();
const results = ref<IResultWithPrices[]>();
const selectedEventId = computed(() => props.eventId);

const resultsApiCall = async () => {
	const response = await api<IRaceResultsResponse>({
		url: `/TrackRacing/GetTrackRacingEventResults?eventId=${selectedEventId.value}&countryCode=${settings.config?.appSetting?.CountryCode}`,
		type: "horses",
		apiVersion: "v1",
		options: { method: "GET" },
	});
	if (!Array.isArray(response.results)) return [];
	return response;
};

const {
	isLoading,
	isFetching,
	refetch: refetchResults,
} = useQuery({
	queryKey: ["EventId", props.eventId],
	queryFn: resultsApiCall,
	onSuccess: (data: IRaceResultsResponse) => {
		results.value =
			(data.results
				.map((result) => ({ ...result, price: data.priceHistory.find((p) => Number(p.outcomeId.outcomeId) === Number(result.outcomeId)) }))
				.sort((a, b) => a.position - b.position) as IResultWithPrices[]) || [];
	},
});

const getOrdinalSuffix = (position: number): string => {
	if (!Number.isInteger(position)) return `${position}`;

	const suffixes = ["th", "st", "nd", "rd"];
	const v = position % 100;
	return `${position}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`;
};

watch(selectedEventId, (newValue, oldValue) => {
	if (newValue && newValue !== oldValue) {
		refetchResults();
	}
});
</script>

<template>
	<OutcomesLoader v-if="isLoading || isFetching" />
	<template v-else>
		<div v-if="results && results.length > 0" class="rounded-lg bg-light-50 dark:bg-dark-800 text-xs">
			<div class="flex justify-between items-center p-2 font-bold min-h-[40px] bg-dark-800 dark:bg-dark-700 rounded-t-lg text-light-50">
				<div class="flex justify-center mr-3">{{ t("position") }}</div>
				<div class="flex justify-center col-end-auto w-[50px] text-center leading-tight">{{ t("starting-price") }}</div>
			</div>
			<div
				v-for="(result, index) in results"
				:key="`result-row-${index}`"
				:class="[
					'flex justify-between py-1 px-2 leading-none items-center min-h-[40px]',
					{ 'border-b border-b-light-400 dark:border-b-dark-500': index + 1 !== results?.length },
				]"
			>
				<div class="flex items-center justify-center">
					<div class="flex font-bold w-8">{{ getOrdinalSuffix(result.position) }}</div>
					<div class="flex justify-center w-10">
						<Silk :isEventActive="false" />
					</div>
					<div class="font-semibold mx-2 text-xs">{{ result.horseName }}</div>
				</div>
				<div
					v-if="result.price"
					class="bg-light-50 dark:bg-dark-400 w-[50px] md:h-8 h-[32px] flex items-center justify-center rounded-md border border-light-500 dark:border-dark-400 font-bold cursor-pointer overflow-hidden text-ellipsis select-none"
				>
					{{ result.price.startingPrice.priceDecimal.toFixed(2) }}
				</div>
			</div>
		</div>
		<div v-else class="w-full text-center text-xs font-bold my-1 py-2">
			{{ t("no-results-available") }}
		</div>
	</template>
</template>
